// app-state.tsx
// Context for managing global application state, including user, EHR context, and authorization.
// Provides a React context and provider for use throughout the application.

import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
  useMemo,
} from "react";
import { WithLauncherProps } from "ehr-launcher-lib";
import { getQueryString } from "@/utils/helpers";
import { getToken } from "@/services/athena";
import { retrieveKeys } from "@/services/access";
import { TokenResponse } from "@/types/auth";
import { ContextDetails } from "@/types/app-state";
import { buildAthenaContext } from "@/utils/token";

const defaultValues = {
  currentUser: process.env.APP_ENV === "local" ? "local" : "",
  loggedInUser: "",
  authorizationError: "",
  isFetchingContext: !!sessionStorage.getItem("token"),
  updateAppState: () => null,
};

/**
 * AppState context value shape.
 */
interface AppState {
  currentUser: string;
  loggedInUser: string;
  context?: ContextDetails;
  authorizationError: string | undefined;
  isFetchingContext: boolean;
  updateAppState: (partial: Partial<AppState>) => void;
}

/**
 * AppStateContext provides global app state, user, and EHR context.
 */
const AppStateContext = createContext<AppState>(defaultValues);

/**
 * AppStateContextProvider wraps children with global app state context.
 * Handles EHR launch, token retrieval, and user context setup.
 * @param children - React children to provide context to
 */
export const AppStateContextProvider: FC<
  PropsWithChildren<WithLauncherProps>
> = ({ children }) => {
  const [appState, updateAppState] = useReducer(
    (state: AppState, changes: Partial<AppState>) => ({
      ...state,
      ...changes,
    }),
    {
      ...defaultValues,
    },
  );

  /**
   * Handles EHR launch, token retrieval, and user context setup.
   *
   * - If running in local mode, retrieves a local token and sets up context for development.
   * - If launched with a launchId but no authCode, redirects to the EHR authorization endpoint.
   * - If launched with an authCode, exchanges it for a token and sets up user context.
   *
   * This effect ensures the application is properly authorized and has the necessary user/EHR context
   * before rendering protected content. It also manages error and loading state for the authorization flow.
   */
  useEffect(() => {
    const launchId = getQueryString("launch");
    const authCode = getQueryString("code");
    const abortController = new AbortController();

    /**
     * LOCAL MODE:
     * If running in local development mode, fetch a local token and set up the context/user.
     * This allows developers to work without a real EHR launch flow.
     */
    if (process.env.REACT_APP_APP_ENV === "local") {
      updateAppState({
        isFetchingContext: true,
      });
      getToken({ code: "local" })
        .then((data: TokenResponse) => {
          const context = buildAthenaContext(data.token);
          sessionStorage.setItem("token", data.token.access_token);
          updateAppState({
            currentUser: context.user,
            context,
            loggedInUser: context.user,
            isFetchingContext: false,
          });
        })
        .catch((error) => {
          console.error(error);
          updateAppState({
            isFetchingContext: false,
            authorizationError: "Failed to authorize user.",
          });
        });
    }

    /**
     * EHR LAUNCH (NO AUTH CODE YET):
     * If the app is launched with a launchId but no authCode, redirect to the EHR's authorization endpoint.
     * This initiates the OAuth2 flow for the EHR.
     */
    if (!authCode && launchId) {
      updateAppState({
        isFetchingContext: true,
      });
      retrieveKeys(
        "ATHENA_CLIENT_ID,ATHENA_AUD,ATHENA_AUTHORIZATION_URI,ATHENA_REDIRECT_URI",
        abortController,
      )
        .then((keys) => {
          window.location.href = `${keys.ATHENA_AUTHORIZATION_URI}?client_id=${keys.ATHENA_CLIENT_ID}&state=asd12344446&scope=launch%20offline_access&redirect_uri=${keys.ATHENA_REDIRECT_URI}&response_type=code&launch=${launchId}&aud=${keys.ATHENA_AUD}`;
        })
        .catch((error) => {
          console.error(error);
          updateAppState({
            isFetchingContext: false,
            authorizationError: "Failed to authorize user.",
          });
        });
    }

    /**
     * AUTH CODE PRESENT:
     * If the app is launched with an authCode (after EHR authorization), exchange it for a token
     * and set up the user/EHR context.
     */
    if (authCode) {
      updateAppState({
        isFetchingContext: true,
      });
      getToken({ code: authCode })
        .then((data: TokenResponse) => {
          const context = buildAthenaContext(data.token);
          sessionStorage.setItem("token", data.token.access_token);
          updateAppState({
            currentUser: context.user,
            context,
            loggedInUser: context.user,
            isFetchingContext: false,
          });
        })
        .catch((error) => {
          console.error(error);
          updateAppState({
            isFetchingContext: false,
            authorizationError: "Failed to authorize user.",
          });
        });
    }

    return () => abortController.abort();
  }, []);

  const value = useMemo(() => ({ ...appState, updateAppState }), [appState]);

  return (
    <AppStateContext.Provider value={value}>
      {children}
    </AppStateContext.Provider>
  );
};

/**
 * useAppState hook for accessing global app state and actions.
 */
export const useAppState = () => useContext(AppStateContext);
